import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Upload, X, Loader2, Music, Plus, Library } from "lucide-react";
import LibraryPicker from "@/components/LibraryPicker";
import { mediaUrl } from "@/lib/api";

export const MediaPreview = ({ src, mime, onClose }) => !src ? null : createPortal(
  <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-6" onClick={onClose} data-testid="attach-preview">
    {mime.startsWith("video/") ? <video src={src} controls autoPlay className="max-h-[88vh] max-w-full" onClick={(e) => e.stopPropagation()} />
      : mime.startsWith("audio/") ? <audio src={src} controls autoPlay onClick={(e) => e.stopPropagation()} />
      : <img src={src} alt="" className="max-h-[88vh] max-w-full object-contain" />}
    <button type="button" onClick={onClose} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="attach-preview-close"><X className="w-5 h-5" /></button>
  </div>, document.body
);

export const Thumb = ({ a, onRemove, size = "h-14 w-14", testid }) => {
  const src = a.preview || mediaUrl(a.url);
  const mime = a.mime || "";
  const [big, setBig] = useState(false);
  return (
    <div className={`relative ${size} rounded-lg overflow-hidden border border-zinc-700 bg-black shrink-0 group ax-fade-up cursor-zoom-in`} data-testid={testid}
      onDoubleClick={() => setBig(true)} title="Double-click to view">
      {big && <MediaPreview src={src} mime={mime} onClose={(e) => { e?.stopPropagation?.(); setBig(false); }} />}
      {mime.startsWith("video/") ? <video src={src} muted className="w-full h-full object-cover" />
        : mime.startsWith("audio/") ? <div className="w-full h-full flex items-center justify-center"><Music className="w-4 h-4 text-zinc-400" /></div>
        : <img src={src} alt="" className="w-full h-full object-cover" />}
      {a.uploading && (
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-0.5" data-testid={`${testid}-uploading`}>
          <Loader2 className="w-4 h-4 animate-spin text-white" />
          {a.progress && <span className="text-[9px] font-mono text-white" data-testid={`${testid}-pct`}>{a.progress.phase === "optimizing" ? "opt" : `${a.progress.pct}%`}</span>}
          {a.progress && <span className="absolute bottom-0 left-0 h-0.5 bg-[#7AF25F] transition-[width] duration-300" style={{ width: `${a.progress.pct}%` }} />}
        </div>
      )}
      {a.duration ? <span className="absolute bottom-0.5 left-0.5 text-[9px] font-mono bg-black/70 px-1 rounded">{Math.round(a.duration)}s</span> : null}
      {onRemove && (
        <button type="button" onClick={onRemove} data-testid={`${testid}-remove`}
          className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center hover:bg-red-500/80">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const fmtEta = (s) => s == null ? "" : s < 60 ? `${s}s left` : `${Math.floor(s / 60)}m ${s % 60}s left`;
const fmtMB = (b) => `${(b / 1048576).toFixed(b > 104857600 ? 0 : 1)} MB`;

export const UploadProgress = ({ a, testid }) => {
  const p = a.progress || {};
  const opt = p.phase === "optimizing";
  return (
    <div className="w-full mt-1" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] font-mono text-zinc-400">
        <span className="truncate max-w-[60%]">{opt ? "Optimizing video…" : `Uploading ${a.size ? fmtMB(a.size) : ""}`}</span>
        <span data-testid={`${testid}-label`}>{opt ? "almost done" : (p.pct || 0) >= 99 ? "Processing…" : `${p.pct || 0}% · ${fmtEta(p.eta) || "estimating…"}`}</span>
      </div>
      <div className="h-1.5 mt-1 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r from-[#7AF25F] to-[#5FC0C8] transition-[width] duration-300 ${opt ? "animate-pulse" : ""}`}
          style={{ width: `${opt ? 100 : p.pct || 0}%` }} data-testid={`${testid}-bar`} />
      </div>
    </div>
  );
};

// Dashed upload zone. items: attachments; max: limit; onFiles(files[])
export default function UploadZone({ title, subtitle, badge, accept, items = [], max = 1, onFiles, onRemove, testid, disabled, onSelect, selected, children }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const [lib, setLib] = useState(false);
  const full = items.length >= max;
  const pick = (files) => {
    const list = Array.from(files || []).slice(0, max - items.length);
    if (list.length) onFiles(list);
  };
  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept={accept} multiple={max > 1} className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
      {items.length === 0 ? (
        <button type="button" disabled={disabled} onClick={() => ref.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files); }}
          className={`w-full rounded-xl border border-dashed px-4 py-6 flex flex-col items-center justify-center gap-2 text-center transition-colors ${
            drag ? "border-[#7AF25F] bg-[#7AF25F]/5" : "border-zinc-700 hover:border-zinc-500 bg-[#0D0D0F]"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
          <span className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center"><Upload className="w-4 h-4 text-zinc-300" /></span>
          <span className="text-sm font-medium text-zinc-200 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">{title}{badge && <span className="ax-badge">{badge}</span>}</span>
          {subtitle && <span className="text-xs text-zinc-500">{subtitle}</span>}
        </button>
      ) : (
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files); }}
          className={`rounded-xl border bg-[#0D0D0F] p-2.5 flex flex-wrap gap-2 items-center ${drag ? "border-[#7AF25F]" : "border-zinc-800"}`}>
          {items.map((a, i) => (onSelect
            ? <div key={a.tid || a.id || a.preview} onClick={() => onSelect(i)} data-testid={`${testid}-select-${i}`}
                className={`rounded-lg cursor-pointer ${selected === i ? "ring-2 ring-[#1e97f3] ring-offset-1 ring-offset-black" : ""}`}><Thumb a={a} testid={`${testid}-thumb-${i}`} onRemove={() => { onRemove(i); }} /></div>
            : <Thumb key={a.tid || a.id || a.preview} a={a} testid={`${testid}-thumb-${i}`} onRemove={() => onRemove(i)} />))}
          {!full && (
            <button type="button" onClick={() => ref.current?.click()} data-testid={`${testid}-add-more`}
              className="h-14 w-14 rounded-lg border border-dashed border-zinc-700 hover:border-zinc-500 flex items-center justify-center text-zinc-400">
              <Plus className="w-4 h-4" />
            </button>
          )}
          {!full && (
            <button type="button" onClick={() => setLib(true)} data-testid={`${testid}-library-more`} title="Pick from Media Library"
              className="h-14 px-3 rounded-lg border border-[#7AF25F]/50 bg-[#7AF25F]/5 hover:bg-[#7AF25F]/10 flex flex-col items-center justify-center gap-0.5 text-[#c9f7bd] text-[10px] font-medium">
              <Library className="w-4 h-4" />Media
            </button>
          )}
          <span className="text-[11px] text-zinc-500 ml-1">{title} · {items.length}/{max}</span>
          {items.map((a, i) => a.uploading && a.progress && (a.size > 2097152 || a.progress.phase === "optimizing")
            ? <UploadProgress key={`p-${a.tid}`} a={a} testid={`${testid}-progress-${i}`} /> : null)}
          {children && <div className="w-full">{children}</div>}
        </div>
      )}
      {items.length === 0 && !disabled && (
        <button type="button" onClick={() => setLib(true)} data-testid={`${testid}-library`}
          className="mt-2 w-full h-9 rounded-lg border border-[#7AF25F]/50 bg-[#7AF25F]/5 text-[#c9f7bd] hover:bg-[#7AF25F]/10 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"><Library className="w-4 h-4" />Attach from Media</button>
      )}
      <LibraryPicker open={lib} onOpenChange={setLib} accept={accept} max={max - items.length} onPick={(refs) => onFiles(refs)} />
    </div>
  );
}
